import reportingSignals, {
  annualReportingSignal,
  quarterlyReportSignal,
  reportDeadlineSignal,
  reportUploadSignal,
  $staticQuarterlyReportsSignal,
  QUARTERLY_REPORT_INIT_STATE,
} from "components/global/Reporting/Reporting.signals";
import { annualReportDeadlines, QUARTERLY_REPORT_DEADLINES } from "./reportConstants";
import alertSignal from "signals/Alert.signal";
import loaderSignal from "signals/Loader.signal";
import api from "api/api";
import { getConfig } from "config/config";
import tokenSignal from "signals/Token.signal";
import {
  LOAN_TERM_RANGES,
  SECURITY_TYPE,
  SEEKING_FUNDING_PAYMENT_RANGES,
  STAGES_OF_INVESTMENT,
  TYPE_OF_VALUATION_CAP,
  YEARS_BUSINESS_OPENED_RANGES
} from "../Constant/constants";
import $appSettings from "signals/AppSettings.signal";
import { baseGraphData } from "./components/ReportingChartsAndTables";
import { readFileAsBase64 } from "../../../libs/functions/global.functions";

export const fetchAndSetStaticQuarterlyReports = async () => {
  loaderSignal.update({ isContentLoading: true });

  try {
    const path = '/reports/quarterly/static';

    const staticQuarterlyReports = await api.get({
      path,
      options: {
        where: {
          platformId: reportingSignals.value.selectedPlatform.id,
        }
      },
    });

    return $staticQuarterlyReportsSignal.update({
      data: staticQuarterlyReports,
    });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
}

export const fetchAndSetQuarterlyReportData = async (e) => {
  loaderSignal.update({ isContentLoading: true });

  const quarterlyReportId = e.target.value;
  try {
    const reportingPeriod = QUARTERLY_REPORT_DEADLINES.find(qrd => qrd.id === quarterlyReportId)
    const [year, quarter] = quarterlyReportId.split('-');

    if (!year || !quarter) {
      return false;
    }

    let path = '/reports/quarterly';
    if (!findMatchingStaticQuarterlyReport(reportingPeriod, reportingSignals.value.selectedPlatform)) {
      path = '/reports/quarterly/caat';
    }

    const quarterlyReport = await api.get({
      path,
      options: {
        where: {
          year_quarter_platformId: {
            platformId: reportingSignals.value.selectedPlatform.id,
            quarter: Number(quarter),
            year: Number(year),
          }
        }
      },
    });

    return quarterlyReportSignal.update({
      quarterlyReport,
      quarterlyReportId
    });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleDeadlineScheduleSelect = (e) => {
  const { value } = e;

  if (value === 'QUARTERLY_SCHEDULE') {
    return reportDeadlineSignal.update(QUARTERLY_REPORT_DEADLINES);
  }
  if (value === 'ANNUAL_SCHEDULE') {
    return reportDeadlineSignal.update(annualReportDeadlines);
  }

  return reportDeadlineSignal.update(null);
};

export const fetchAnnualReportYearOptions = async () => {
  try {
    const yearOptions = await api.get({
      path: '/annualReportDrafts/year-options',
    });
    annualReportingSignal.update({ yearOptions });
    return yearOptions;
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
    return null;
  }
};

export const fetchAndSetAnnualReport = async (selectedYear = null) => {
  try {
    const { yearOptions } = annualReportingSignal.value;
    if (!yearOptions) {
      await fetchAnnualReportYearOptions();
    }

    annualReportingSignal.update({
      fetchingTableData: true,
      selectedYear: selectedYear ?? null,
    });

    const platformId = reportingSignals.value.selectedPlatform?.id;
    const yearParam = selectedYear != null ? `year=${selectedYear}` : '';
    const platformParam = platformId != null ? `platformId=${platformId}` : '';
    const params = [yearParam, platformParam].filter(Boolean).join('&');
    const path = params ? `/annualReportDrafts?${params}` : '/annualReportDrafts';
    const reportData = await api.get({ path });

    // Remove CAP Transactions from the static list; it uses a paginated endpoint.
    const filteredReportData = reportData.filter((b) => b.name !== 'CAP Transactions');

    const currentTab = annualReportingSignal.value.selectedTab;
    const capTransactionsName = 'CAP Transactions';
    const stayOnCapTransactions =
      currentTab === capTransactionsName &&
      reportData.some((b) => b.name === capTransactionsName);
    const nextTab = stayOnCapTransactions
      ? capTransactionsName
      : filteredReportData[0]?.name || '';

    if (stayOnCapTransactions) {
      await fetchCapTransactionsPaginated(selectedYear, 1);
    }

    return annualReportingSignal.update({
      selectedTab: nextTab,
      fetchingTableData: false,
      reportData: filteredReportData,
    });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });

    annualReportingSignal.update({
      fetchingTableData: false,
    });
  }
};

export const fetchProviderDataPaginated = async (page = 1, pageSize = 20) => {
  try {
    annualReportingSignal.update({
      providerData: {
        ...annualReportingSignal.value.providerData,
        loading: true,
      },
    });

    const platformId = reportingSignals.value.selectedPlatform?.id;
    const platformParam = platformId != null ? `&platformId=${platformId}` : '';
    const path = `/annualReportDrafts/providers?page=${page}&pageSize=${pageSize}${platformParam}`;
    const result = await api.get({ path });

    const firstRow = result.data[0];
    const extractedHeaders = firstRow
      ? Object.entries(firstRow)
          .filter(([key]) => key !== 'id' && key !== 'recordType')
          .map(([key, fieldData]) => ({
            key,
            label:
              typeof fieldData === 'object' && fieldData?.label
                ? fieldData.label
                : key.split(/(?=[A-Z])/).join(' ').replace(/^\w/, (c) => c.toUpperCase()),
          }))
      : [];

    return annualReportingSignal.update({
      providerData: {
        data: result.data,
        headers: extractedHeaders,
        pagination: result.pagination,
        loading: false,
      },
    });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });

    annualReportingSignal.update({
      providerData: {
        ...annualReportingSignal.value.providerData,
        loading: false,
      },
    });
  }
};

export const fetchCapTransactionsPaginated = async (selectedYear = null, page = 1, pageSize = 20) => {
  try {
    annualReportingSignal.update({
      capTransactions: {
        ...annualReportingSignal.value.capTransactions,
        loading: true,
      },
    });

    const platformId = reportingSignals.value.selectedPlatform?.id;
    const yearParam = selectedYear != null ? `&year=${selectedYear}` : '';
    const platformParam = platformId != null ? `&platformId=${platformId}` : '';
    const path = `/annualReportDrafts/capTransactions?page=${page}&pageSize=${pageSize}${yearParam}${platformParam}`;
    const result = await api.get({ path });

    const firstRow = result.data[0];
    const extractedHeaders = firstRow
      ? Object.entries(firstRow)
          .filter(([key]) => key !== 'id' && key !== 'recordType')
          .map(([key, fieldData]) => ({
            key,
            label: typeof fieldData === 'object' && fieldData?.label
              ? fieldData.label
              : key.split(/(?=[A-Z])/).join(' ').replace(/^\w/, c => c.toUpperCase()),
          }))
      : [];

    return annualReportingSignal.update({
      capTransactions: {
        data: result.data,
        headers: extractedHeaders,
        pagination: result.pagination,
        loading: false,
      },
    });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });

    annualReportingSignal.update({
      capTransactions: {
        ...annualReportingSignal.value.capTransactions,
        loading: false,
      },
    });
  }
};

export const fetchAndSetPlatforms = async () => {
  loaderSignal.update({ isContentLoading: true });

  try {
    const platforms = await api.get({
      path: '/platforms',
      options: {
        include: {
          fundingPrograms: {
            include: {
              referenceProgram: true,
            },
          },
          tranches: true,
          quarterlyReports: {
            select: {
              id: true,
              year: true,
              quarter: true,
            }
          }
        },
      },
    });
    reportingSignals.update({ platforms });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const fetchAndSetPotentialAllocation = async () => {
  loaderSignal.update({ isContentLoading: true });
  try {
    const preliminaryAllocation = await api.get({
      path: '/allocations',
      options: {
        where: {
          jurisdiction: 'Louisiana',
        },
      },
    });
    const totalPotentialAllocation =
      preliminaryAllocation[0].totalPotentialFundingAmount;
    return Number(totalPotentialAllocation);
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

const REBUILD_SIMULATED_STEPS = [
  { pct: 0, step: 'Provider' },
  { pct: 10, step: 'CAP Transactions' },
  { pct: 40, step: 'Loan-Credit OCSP' },
  { pct: 50, step: 'VC-Equity OCSP' },
  { pct: 60, step: 'Loan-Investment Performance' },
  { pct: 70, step: 'Borrower-Investee Data' },
  { pct: 80, step: 'Assembling report' },
  { pct: 90, step: 'Finalizing' },
];
const SIMULATED_STEP_INTERVAL_MS = 2500;

const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 0));

const rebuildWithProgress = async (pathWithStream, onProgress, onRealProgress) => {
  const baseUrl = getConfig('REACT_APP_API_ENDPOINT');
  const res = await fetch(baseUrl + pathWithStream, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${tokenSignal.value.accessToken || ''}`,
      'Content-Type': 'application/json',
      appId: getConfig('APP_CONFIG').appId,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    let message = text || res.statusText;
    try {
      const json = JSON.parse(text);
      if (json && typeof json.message === 'string') message = json.message;
    } catch (_) {}
    throw new Error(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    const chunkUpdates = [];
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const obj = JSON.parse(line);
        if (obj.error) throw new Error(obj.message || 'Rebuild failed');
        if (typeof obj.progress === 'number') {
          chunkUpdates.push({ progress: obj.progress, step: obj.step });
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue;
        throw e;
      }
    }
    if (chunkUpdates.length > 0 && onProgress) {
      if (typeof onRealProgress === 'function') onRealProgress();
      const latest = chunkUpdates.reduce((best, u) => (u.progress > best.progress ? u : best), chunkUpdates[0]);
      await yieldToMain();
      onProgress(latest.progress, latest.step);
    }
  }
  if (buffer.trim()) {
    const lines = buffer.trim().split('\n');
    const chunkUpdates = [];
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const obj = JSON.parse(line);
        if (obj.error) throw new Error(obj.message || 'Rebuild failed');
        if (typeof obj.progress === 'number') {
          chunkUpdates.push({ progress: obj.progress, step: obj.step });
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue;
        throw e;
      }
    }
    if (chunkUpdates.length > 0 && onProgress) {
      if (typeof onRealProgress === 'function') onRealProgress();
      const latest = chunkUpdates.reduce((best, u) => (u.progress > best.progress ? u : best), chunkUpdates[0]);
      await yieldToMain();
      onProgress(latest.progress, latest.step);
    }
  }
};

export const handleDownloadAnnualReport = async () => {
  const { selectedYear } = annualReportingSignal.value;
  const platformId = reportingSignals.value.selectedPlatform?.id;
  annualReportingSignal.update({ downloadingReport: true, downloadProgress: 0, downloadStep: 'Provider' });

  let stepIndex = 0;
  const simulationIntervalId = setInterval(() => {
    if (stepIndex >= REBUILD_SIMULATED_STEPS.length) return;
    const step = REBUILD_SIMULATED_STEPS[stepIndex];
    annualReportingSignal.update({ downloadProgress: step.pct, downloadStep: step.step });
    stepIndex += 1;
  }, SIMULATED_STEP_INTERVAL_MS);

  const clearSimulation = () => {
    clearInterval(simulationIntervalId);
  };

  try {
    const yearParam = selectedYear != null ? `year=${selectedYear}` : '';
    const platformParam = platformId != null ? `platformId=${platformId}` : '';
    const params = [yearParam, platformParam, 'full=true', 'stream=1'].filter(Boolean).join('&');
    const rebuildPath = `/annualReportDrafts/rebuild?${params}`;

    await rebuildWithProgress(rebuildPath, (progress, step) => {
      annualReportingSignal.update({ downloadProgress: progress, downloadStep: step || '' });
    }, clearSimulation);

    await api.get({
      path: '/annualReportDrafts/download',
      isFileDownload: true,
    });
    annualReportingSignal.update({ downloadProgress: 100, downloadStep: 'Download complete' });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    clearInterval(simulationIntervalId);
    annualReportingSignal.update({ downloadingReport: false, downloadProgress: 0, downloadStep: '' });
  }
};

export const downloadQuarterlyReport = async () => {
  loaderSignal.update({
    isContentLoading: true,
    message:
      'Generating Quarterly Report File for Download. This may take several minutes.',
  });
  try {
    const { quarterlyReport, quarterlyReportId } = quarterlyReportSignal.value;
    const reportingPeriod = QUARTERLY_REPORT_DEADLINES.find(qrd => qrd.id === quarterlyReportId);
    const options = {};
    let path = '/reports/quarterly/download'

    if (findMatchingStaticQuarterlyReport(reportingPeriod, reportingSignals.value.selectedPlatform)) {
      options.where = {
        id: quarterlyReport.id
      }
    } else {
      const [year, quarter] = quarterlyReportId.split('-');
      if (!year || !quarter) {
        return false;
      }

      path = '/reports/quarterly/download/caat'
      options.where = {
        year_quarter_platformId: {
          platformId: reportingSignals.value.selectedPlatform.id,
          quarter: Number(quarter),
          year: Number(year),
        }
      }
    }

    await api.get({
      path,
      isFileDownload: true,
      options,
    });
    return alertSignal.update({
      type: 'notification',
      message:
        'Successfully Generated Quarterly Report file. Check your downloads folder',
      variant: 'success',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleQuarterlyReportUpload = (e) => {
  const { files } = e.target;

  reportUploadSignal.update({ files });
};

export const handleUploadQuarterlyReport = async (fileInputRef) => {
  loaderSignal.update({
    isContentLoading: true,
    message: 'Uploading report...',
  });

  const base64 = await readFileAsBase64(reportUploadSignal.value.files[0]);
  const body = {
    data: {
      platformId: reportingSignals.value.selectedPlatform.id,
      quarter: Number(reportUploadSignal.value.quarter),
      year: Number(reportUploadSignal.value.year),
      base64,
    }
  }

  try {
    await api.post({
      path: '/reports/quarterly',
      body,
    });

    await fetchAndSetPlatforms();
    alertSignal.update({
      type: 'notification',
      variant: 'success',
      message: 'Successfully uploaded report!',
    });
    reportUploadSignal.reset();
    fileInputRef.current = null;
  } catch (error) {
    alertSignal.update({
      type: 'alert',
      message: `Quarterly report upload requirements are strict. Please make sure you are using the correct template. If you need further guidance, please contact support. ERROR: ${error.message}`,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleQuarterReportUploadSelectChange = (e) => {
  const { name, value } = e;

  reportUploadSignal.update({
    [name]: value,
  });
};

export const getProviderTypeTally = (data) => {
  const { PROVIDER_TYPES } = $appSettings.value.constants;
  const { providerType } = baseGraphData;
  const labelOrder = Object.values(PROVIDER_TYPES).map((value) => value.value);

  const tally = data.reduce((accumulator, currentValue) => {
    const providerType = currentValue.providerType.value;
    if (labelOrder.includes(providerType)) {
      accumulator[providerType] = (accumulator[providerType] || 0) + 1;
    } else {
      accumulator['Other'] = (accumulator['Other'] || 0) + 1;
    }
    return accumulator;
  }, {});

  const labels = Object.values(PROVIDER_TYPES).map((value) => value.annualReportLabel);

  return {
    ...providerType,
    labels,
    datasets: [{
      ...providerType.datasets[0],
      data: [...labelOrder.map((label) => tally[label] || 0), tally['Other'] || 0],
    }]
  };
};

export const getMinorityDepositoryTally = (data) => {
  const { minorityDepository } = baseGraphData;
  const tally = data.reduce((accumulator, currentValue) => {
    const status = currentValue.minorityDepositoryInstitution.value ? 'Yes' : 'No';
    accumulator[status] = (accumulator[status] || 0) + 1;
    return accumulator;
  }, {});

  return {
    ...minorityDepository,
    datasets: [
      {
        ...minorityDepository.datasets[0],
        data: [tally.Yes || 0, tally.No || 0],
      }
    ],
  };
};

export const getTargetFundSizeTally = (data) => {
  const { targetFundSize } = baseGraphData;
  const fundSizeRanges = SEEKING_FUNDING_PAYMENT_RANGES.map((range) => {
    const [min, max] = range.replace(/\$/g, '').split(' - ');
    return {
      min: parseInt(min),
      max: max ? parseInt(max) : null,
      label: range,
    };
  });

  const tally = {};
  fundSizeRanges.forEach((range) => (tally[range.label] = 0));

  data.forEach((currentValue) => {
    const targetFundSize = currentValue.targetFundSize.value;

    if (!targetFundSize) {
    } else {
      if (parseInt(targetFundSize) > 100000) {
        tally[fundSizeRanges[fundSizeRanges.length - 1].label] =
          tally[fundSizeRanges[fundSizeRanges.length - 1].label] + 1;
      } else {
        const fundRange = fundSizeRanges.find(
          (range) => targetFundSize >= range.min && targetFundSize <= range.max
        );
        tally[fundRange.label] = tally[fundRange.label] + 1;
      }
    }
  });

  return {
    ...targetFundSize,
    datasets: [
      {
        ...targetFundSize.datasets[0],
        data: [...fundSizeRanges.map(fundSizeRange => tally[fundSizeRange.label])],
      },
    ],
  };
};

export const getYearBusinessOpenedTally = (data) => {
  const { yearOpened } = baseGraphData;
  const yearRanges = YEARS_BUSINESS_OPENED_RANGES.map((range) => {
    const [min, max] = range.split('-');
    return {
      min: parseInt(min),
      max: max ? parseInt(max) : null,
      label: range,
    };
  });

  const tally = {};
  yearRanges.forEach((range) => (tally[range.label] = 0));

  data.forEach((currentValue) => {
    const yearBusinessOpened = currentValue.yearBusinessOpened.value;

    if (!yearBusinessOpened) {
    } else {
      if (yearBusinessOpened > 2020) {
        tally[yearRanges[yearRanges.length - 1].label] =
          tally[yearRanges[yearRanges.length - 1].label] + 1;
      } else {
        const yearRange = yearRanges.find(
          (range) =>
            yearBusinessOpened >= range.min && yearBusinessOpened <= range.max
        );
        if (!yearRange) {
          return;
        }
        tally[yearRange.label] = tally[yearRange.label] + 1;
      }
    }
  });

  return {
    ...yearOpened,
    datasets: [
      {
        ...yearOpened.datasets[0],
        data: [...yearRanges.map(range => tally[range.label])],
      }
    ]
  }
};

export const getFormOfBusinessTally = (data) => {
  const { formOfBusinessOrganization } = baseGraphData;
  const { FORM_OF_BUSINESS_ORGANIZATION } = $appSettings.value.constants;
  const enumValues = Object.values(FORM_OF_BUSINESS_ORGANIZATION).map(type => type.value)
  const labelOrder = Object.values(FORM_OF_BUSINESS_ORGANIZATION).map(type => type.annualReportLabel);
  const tally = data.reduce((accumulator, currentValue) => {
    const formOfBusinessOrganization =
      currentValue.formOfBusinessOrganization.value;

    if (formOfBusinessOrganization) {
      accumulator[formOfBusinessOrganization] =
        (accumulator[formOfBusinessOrganization] || 0) + 1;
    }
    return accumulator;
  }, {});

  return {
    ...formOfBusinessOrganization,
    labels: labelOrder,
    datasets: [
      {
        ...formOfBusinessOrganization.datasets[0],
        data: [...enumValues.map((label) => tally[label] || 0)],
      },
    ],
  };
};

export const getLoanTypeTally = (data) => {
  const { loanType } = baseGraphData;
  const { LOAN_TYPES } = $appSettings.value.constants;
  const enumValues = Object.values(LOAN_TYPES).map(type => type.value);
  const labelOrder = Object.values(LOAN_TYPES).map(type => type.annualReportLabel);

  const tally = data.reduce((accumulator, currentValue) => {
    const loanType = currentValue.loanType.value;

    if (loanType) {
      accumulator[loanType] = (accumulator[loanType] || 0) + 1;
    }
    return accumulator;
  }, {});

  return {
    ...loanType,
    labels: labelOrder,
    datasets: [
      {
        ...loanType.datasets[0],
        data: [...enumValues.map((label) => tally[label] || 0)],
      },
    ],
  };
};

export const getLoanTermTally = (data) => {
  const { loanTerm } = baseGraphData;
  const loanTermRanges = LOAN_TERM_RANGES.map((range) => {
    const [min, max] = range.split('-');
    return {
      min: parseInt(min),
      max: max ? parseInt(max) : null,
      label: range,
    };
  });

  const tally = {};
  loanTermRanges.forEach((range) => (tally[range.label] = 0));

  data.forEach((currentValue) => {
    const loanTerm = currentValue.loanTerm.value ? currentValue.loanTerm.value / 12 : null;

    if (!loanTerm) {

    } else {
      if (loanTerm > 10) {
        tally[loanTermRanges[loanTermRanges.length - 1].label] =
          tally[loanTermRanges[loanTermRanges.length - 1].label] + 1;
      } else {
        const loanTermRange = loanTermRanges.find(
          (range) => loanTerm >= range.min && loanTerm <= range.max
        );
        tally[loanTermRange.label] = tally[loanTermRange.label] + 1;
      }
    }
  });

  return {
    ...loanTerm,
    datasets: [
      {
        ...loanTerm.datasets[0],
        data: [...loanTermRanges.map(range => tally[range.label])],
      }
    ]
  }
};

export const getStageOfInvestmentTally = (data) => {
  const { stageOfInvestment } = baseGraphData;
  const labelOrder = Object.values(STAGES_OF_INVESTMENT).map(value => value);

  const tally = data.reduce((accumulator, currentValue) => {
    const stageOfInvestmentValue = currentValue.stageOfInvestment.value;
    if (stageOfInvestmentValue) {
      accumulator[stageOfInvestmentValue] = (accumulator[stageOfInvestmentValue] || 0) + 1;
    }
    return accumulator;
  }, {});

  return {
    ...stageOfInvestment,
    datasets: [
      {
        ...stageOfInvestment.datasets[0],
        data: [...labelOrder.map((label) => tally[label] || 0)],
      },
    ],
  }
};

export const getSecurityTypeTally = (data) => {
  const { securityType } = baseGraphData;
  const labelOrder = Object.values(SECURITY_TYPE).map(value => value);

  const tally = data.reduce((accumulator, currentValue) => {
    const securityTypeValue = currentValue.securityType.value;
    if (securityTypeValue) {
      accumulator[securityTypeValue] = (accumulator[securityTypeValue] || 0) + 1;
    }
    return accumulator;
  }, {});

  return {
    ...securityType,
    datasets: [
      {
        ...securityType.datasets[0],
        data: [...labelOrder.map((label) => tally[label] || 0)],
      },
    ],
  }
};

export const getValuationCapTally = (data) => {
  const { valuationType } = baseGraphData;
  const labelOrder = Object.values(TYPE_OF_VALUATION_CAP).map(value => value);

  const tally = data.reduce((accumulator, currentValue) => {
    const valuationCapType = currentValue.typeOfValuationCap.value;
    if (valuationCapType) {
      accumulator[valuationCapType] = (accumulator[valuationCapType] || 0) + 1;
    }
    return accumulator;
  }, {});

  return {
    ...valuationType,
    datasets: [
      {
        ...valuationType.datasets[0],
        data: [...labelOrder.map((label) => tally[label] || 0)],
      },
    ],
  }
};

export const handlePlatformSelection = async (e) => {
  const { platforms } = reportingSignals.value;
  const { value } = e.target;

  reportingSignals.update({
    selectedPlatform: platforms.find((p) => p.id === Number(value)),
  });

  quarterlyReportSignal.update({
    quarterlyReportId: '',
    quarterlyReport: QUARTERLY_REPORT_INIT_STATE,
  });
  $staticQuarterlyReportsSignal.update({ data: [] });
  annualReportingSignal.update({
    reportData: null,
    providerData: {
      data: [],
      headers: [],
      pagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 },
      loading: false,
    },
    capTransactions: {
      data: [],
      headers: [],
      pagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 },
      loading: false,
    },
  });

  await fetchAndSetAnnualReport();
};

export const showUploadQuarterlyReportModal = () => reportUploadSignal.update({
  modalVisible: true
})
export const hideUploadQuarterlyReportModal = () => reportUploadSignal.update({
  modalVisible: false
})

export const reportingPeriodIsCurrentOrPast = (period) => {
  const [start, end] = period.split(' to ');
  const currentDate = new Date();
  const startDate = new Date(start);
  const endDate = new Date(end);

  if (startDate < currentDate && currentDate < endDate) {
    return 'current';
  } else if (endDate < currentDate) {
    return 'past';
  }

  return false;
}

export function findMatchingStaticQuarterlyReport(quarterlyReportDeadline, platform) {
  if (!platform || !quarterlyReportDeadline) {
    return false;
  }
  if (!Array.isArray(platform.quarterlyReports)) {
    return false;
  }

  return platform.quarterlyReports.find(
    (qr) => `${qr.year}-${qr.quarter}` === quarterlyReportDeadline.id
  );
}
